import { ThemeType } from "../../constants";

// Cms Controls
// -------------------------------------------------------------------------
// NOTE: Since we are using React Router to display pages which will load in the
// containers and controls we are going to render, relying on a redux action to update
// the currentPage key on the cmsSettings object will cause REALLY BAD ERRORS because either
// the currently rendered controls will lose their options or the new controls will try to load in
// with the incorrect control options.
//
// The fix is that since we know what pages these components are supposed to be loaded on,
// we can set which containers the controls should be coming from with DefaultView
// -------------------------------------------------------------------------
export const selectCmsSettingsSelector = (state) => state.cmsSettings;

export const selectContainersSelector = (pages = {}) =>
    Object.keys(pages)
        .filter((key) => key !== "themeLevelControls")
        .flatMap((key) => pages[key]);

export const selectControlsSelector = (containers) => {
    const controls = containers.flatMap((container) => container.controls);
    return controls;
};

export const selectContainersByPageSelector = (state, page) => {
    const cmsSettings = selectCmsSettingsSelector(state);
    const { pages } = cmsSettings;
    const containers = pages[page];
    return containers;
};

export const selectCmsControlsSelector = (state) => {
    const cmsSettings = selectCmsSettingsSelector(state);
    const { pages } = cmsSettings;
    const containers = selectContainersSelector(pages);
    const controls = selectControlsSelector(containers);
    return controls;
};

export const selectCmsControlsByPageSelector = (state, page) => {
    const containers = selectContainersByPageSelector(state, page);
    const controls = selectControlsSelector(containers);
    return controls;
};

export const selectCmsControlByIdByPageSelector = (id, page, state) => {
    const controls = selectCmsControlsByPageSelector(state, page);
    const rightControl = controls.find((control) => control.id === id);
    return rightControl || {};
};

// Note: only use this selector to grab a control that there will only ever
//       be one of per registration form. E.g controls that are only created
//       in cms.views.php and are single-use controls, that will never be
//       repeated
export const selectCmsControlByTypeSelector = (cmsControlEnum, state) => {
    const controls = selectCmsControlsSelector(state);
    const control = controls.find(({ type }) => type === cmsControlEnum);
    return control || {};
};

// -------------------------------------------------------------------------
// NOTE: This reducer is only used by controls that can be on more than one page
// Currently, those are only the widgets:
// ConnectedWidgetHeadingControl, ConnectedWidgetCustomContentControl,
// ConnectedWidgetSpacingControl, and ConnectedWidgetTextControl
// -------------------------------------------------------------------------

export const selectCmsControlByIdSelector = (id, state) => {
    const cmsSettings = selectCmsSettingsSelector(state);
    const pages = Object.keys(cmsSettings.pages).filter(
        (page) => page !== "themeLevelControls",
    );
    const containers = [];
    pages.forEach((page) => containers.push(cmsSettings.pages[page]));
    let controls = [];
    containers.forEach((container) => {
        controls.push(selectControlsSelector(container));
    });
    controls = controls.flatMap((control) => control);
    const rightControl = controls.find((control) => control.id === id);
    return rightControl || {};
};

export const selectThemeSettingsSelector = (theme, state) => {
    const cmsSettings = selectCmsSettingsSelector(state);
    const { themeLevelControls } = cmsSettings;
    let themeSettings = {};
    switch (theme) {
        case ThemeType.HEADER:
            themeSettings = themeLevelControls.registrationHeader;
            break;
        case ThemeType.SUMMARY:
            themeSettings = themeLevelControls.registrationSummary;
            break;
        case ThemeType.FOOTER:
            themeSettings = themeLevelControls.registrationFooter;
            break;
        default:
    }
    return themeSettings;
};

// -------------------------------------------------------------------------
// Note: selectCmsOptionByType has a very specific use case:
// 1. You need to find a cms option value for a TYPE of control
// 2. There are more than one TYPE of those controls in cms settings
// 3. You can use the first control's option
//
// An example would be the early submission of the form
// when that early submit flag is true, we display the cms option for
// "ifNoTotalCompleteButtonLabel" from the payment page footer control
// -------------------------------------------------------------------------
export const selectCmsOptionByTypeSelector = (type, option, state) => {
    let cmsValue = "";
    const controls = selectCmsControlsSelector(state);
    const typeControls = controls
        .filter((control) => control && control.type && control.type === type)
        .filter((control) => Object.keys(control.options).includes(option));
    if (typeControls.length > 0) {
        cmsValue = typeControls[0].options[option];
    }
    return cmsValue;
};
